import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, typographyStyles, Tooltip } from "@fluentui/react-components";
import {
    Dismiss16Regular,
    ChevronDown16Regular,
    ChevronRight16Regular,
    ArrowLeft16Regular,
    FolderGlobe16Regular,
} from "@fluentui/react-icons";
import { NewformaButton } from "../../../_base/Button";
import { NewformaTable, NewformaTableBody, NewformaTableRow, NewformaTableCell } from "../../../_base/Table";
import { AttachmentItem } from "../../../../models/shared/AttachmentList";
import { Logger } from "../../../../services/Logger";
import { AttachmentVisual, BackButton } from "./AttachmentListComponentTypes";
import {
    prepareFoldersStructure,
    findInHierarchy,
    createAttachmentVisuals,
    sortAttachmentVisuals,
    createBackButton,
    resolveBackButton,
    getItemsToRemove,
    ROOT_FOLDER_KEY,
    MAX_ATTACHMENTS,
} from "./AttachmentListHelpers";
import { getAttachmentIcon } from "./FileTypeIcons";

export interface AttachmentListComponentProps extends LocalizeContextProps {
    disabled: boolean;
    attachmentsItems: AttachmentItem[];
    onItemRemove: (attachments: AttachmentItem[]) => void;
    onClear: () => void;
    logger: Logger;
    onMoveIntoSubFolder?: () => void;
    onReturnToRoot?: () => void;
    /** Override the max count shown in the header. Defaults to MAX_ATTACHMENTS (25). */
    maxAttachments?: number;
    /** Increment to force the list back to the root navigation level. */
    resetKey?: number;
}

const useStyles = makeStyles({
    wrapper: {
        backgroundColor: "transparent",
        border: "none",
        width: "100%",
        padding: "0",
        paddingTop: tokens.spacingVerticalL,
    },
    groupHeader: {
        paddingBottom: "6px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        display: "flex",
        alignItems: "center",
        position: "relative",
    },
    chevron: {
        fontSize: "16px",
        position: "absolute",
        top: "2px",
        left: "4px",
        cursor: "pointer",
        color: tokens.colorNeutralForeground1,
    },
    headerText: {
        ...typographyStyles.caption1Strong,
        color: tokens.colorNeutralBackgroundInverted,
        paddingLeft: "30px",
    },
    headerCount: {
        fontWeight: tokens.fontWeightRegular,
    },
    clearAllContainer: {
        marginLeft: "auto",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
    },
    clearAllText: {
        ...typographyStyles.caption1,
        color: tokens.colorBrandForegroundLink,
    },
    backBar: {
        ...typographyStyles.caption1,
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        marginTop: "6px",
    },
    backButton: {
        ...typographyStyles.caption1,
        minWidth: "unset",
        flexShrink: 0,
        paddingLeft: "4px",
        paddingRight: "8px",
        height: "24px",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    backFolderNameContainer: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        flex: 1,
        minWidth: 0,
    },
    backFolderName: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: 1,
    },
    removeIcon: {
        width: "24px",
        fontSize: "14px",
        cursor: "pointer",
    },
    fileIcon: {
        color: tokens.colorBrandForeground1,
    },
    projectSourceIcon: {
        width: "16px",
        height: "16px",
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
        display: "block",
    },
    attachmentName: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
    },
    folderName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        cursor: "pointer",
        color: tokens.colorBrandForeground1,
    },
    tableContainer: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
        marginTop: "10px",
        flexBasis: "auto",
        width: "auto",
        marginBottom: "7px",
        alignItems: "center",
    },
    scrollContainer: {
        maxHeight: "235px",
        overflowY: "auto",
        overflowX: "hidden",
    },
});

const AttachmentsListFluent9Inner = (props: AttachmentListComponentProps) => {
    const styles = useStyles();

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [currentFolderKey, setCurrentFolderKey] = useState<string | undefined>(undefined);
    const [backButton, setBackButton] = useState<BackButton | undefined>(undefined);

    // Build hierarchy whenever attachmentsItems changes
    const hierarchy = useMemo(() => prepareFoldersStructure(props.attachmentsItems), [props.attachmentsItems]);

    // Derive visible items + shouldShowTotalAndClearAll from currentFolderKey
    const { items, shouldShowTotalAndClearAll } = useMemo(() => {
        if (!currentFolderKey) {
            // Root level
            const visuals = createAttachmentVisuals(hierarchy.children).sort(sortAttachmentVisuals);
            return { items: visuals, shouldShowTotalAndClearAll: true };
        }
        // Inside a subfolder
        const hierarchyItem = findInHierarchy(hierarchy, currentFolderKey);
        if (!hierarchyItem) {
            const visuals = createAttachmentVisuals(hierarchy.children).sort(sortAttachmentVisuals);
            return { items: visuals, shouldShowTotalAndClearAll: true };
        }
        const visuals = createAttachmentVisuals(hierarchyItem.children).sort(sortAttachmentVisuals);
        return { items: visuals, shouldShowTotalAndClearAll: false };
    }, [hierarchy, currentFolderKey]);

    const navigateToFolder = useCallback(
        (folderKey: string, folderName: string) => {
            props.onMoveIntoSubFolder?.();
            const hierarchyItem = findInHierarchy(hierarchy, folderKey);
            if (!hierarchyItem) {
                props.logger.warning(
                    `[onFolderClick] Something went wrong because can't find the attachment ${folderKey} in the hierarchy.`,
                    hierarchyItem
                );
                return;
            }
            const parentKey = hierarchyItem.parent?.path ?? ROOT_FOLDER_KEY;
            setCurrentFolderKey(folderKey);
            setBackButton(createBackButton(folderName, folderKey, parentKey));
        },
        [hierarchy, props.onMoveIntoSubFolder, props.logger]
    );

    const navigateToRoot = useCallback(() => {
        props.onReturnToRoot?.();
        setCurrentFolderKey(undefined);
        setBackButton(undefined);
    }, [props.onReturnToRoot]);

    // Reset to root whenever resetKey increments (e.g. user clicks "Select project files" from a subfolder)
    const prevResetKeyRef = React.useRef(props.resetKey);
    useEffect(() => {
        if (props.resetKey !== undefined && props.resetKey !== prevResetKeyRef.current) {
            prevResetKeyRef.current = props.resetKey;
            setCurrentFolderKey(undefined);
            setBackButton(undefined);
        }
    }, [props.resetKey]);

    // When attachments change, resolve the back button (e.g. after item removal)
    const prevLengthRef = React.useRef(props.attachmentsItems.length);
    useEffect(() => {
        if (props.attachmentsItems.length !== prevLengthRef.current) {
            prevLengthRef.current = props.attachmentsItems.length;
            const resolved = resolveBackButton(backButton, props.attachmentsItems);
            if (resolved) {
                navigateToFolder(resolved.folderKey, resolved.folderName);
            } else {
                navigateToRoot();
            }
        }
    }, [props.attachmentsItems.length, backButton, navigateToFolder, navigateToRoot]);

    const onFolderClick = (item: AttachmentVisual) => {
        navigateToFolder(item.key, item.name);
    };

    const onBackClick = (back: BackButton) => {
        const foundHierarchyItem = findInHierarchy(hierarchy, back.parentKey);
        if (!foundHierarchyItem) {
            props.logger.warning(
                `[onBackClick] Something went wrong because can't find the attachment ${back.parentKey} in the hierarchy.`,
                foundHierarchyItem
            );
            return;
        }
        if (!foundHierarchyItem.parent) {
            navigateToRoot();
            return;
        }
        setCurrentFolderKey(foundHierarchyItem.path);
        setBackButton(
            createBackButton(foundHierarchyItem.name, foundHierarchyItem.path, foundHierarchyItem.parent.path)
        );
    };

    const onItemRemove = (attachment: AttachmentVisual) => {
        const itemsToRemove = getItemsToRemove(attachment, props.attachmentsItems);
        props.onItemRemove(itemsToRemove);
    };

    const onClear = () => {
        if (!props.disabled) {
            props.onClear();
        }
    };

    const toggleCollapse = () => {
        setIsCollapsed((prev) => !prev);
    };

    const renderGroupHeader = () => (
        <div className={styles.groupHeader}>
            {isCollapsed ? (
                <ChevronRight16Regular
                    className={styles.chevron}
                    onClick={toggleCollapse}
                    data-testid="project-files-attachment-list-toggle"
                />
            ) : (
                <ChevronDown16Regular
                    className={styles.chevron}
                    onClick={toggleCollapse}
                    data-testid="project-files-attachment-list-toggle"
                />
            )}
            <span className={styles.headerText}>
                {props.translate("SHARED.ATTACHMENTS.ATTACHMENTS_LABEL") as string}{" "}
                {props.attachmentsItems.length > 0 && shouldShowTotalAndClearAll ? (
                    <span className={styles.headerCount}>{` (${
                        props.attachmentsItems.length
                    } / ${props.maxAttachments ?? MAX_ATTACHMENTS})`}</span>
                ) : null}
            </span>
            {shouldShowTotalAndClearAll ? (
                <div
                    className={styles.clearAllContainer}
                    onClick={onClear}
                    data-testid="project-files-attachment-list-clear"
                >
                    <span className={styles.clearAllText}>{props.translate("SHARED.ATTACHMENTS.CLEAR") as string}</span>
                </div>
            ) : null}
        </div>
    );

    const renderBackBar = () => {
        if (!backButton || isCollapsed) return null;
        return (
            <div className={styles.backBar} data-testid="project-files-attachment-list-back-bar">
                <NewformaButton
                    className={styles.backButton}
                    appearance="subtle"
                    icon={<ArrowLeft16Regular />}
                    onClick={() => onBackClick(backButton)}
                    testid="project-files-attachment-list-back-button"
                >
                    {props.translate("SHARED.ATTACHMENTS.BACK").toString()}
                </NewformaButton>
                <div className={styles.backFolderNameContainer}>
                    {getAttachmentIcon({ type: "folder", key: "back-bar", name: backButton.folderName })}
                    <span className={styles.backFolderName}>{backButton.folderName}</span>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.wrapper} data-is-scrollable={true} data-testid="project-files-attachment-list">
            <div className={styles.tableContainer}>
                {renderGroupHeader()}
                {renderBackBar()}
                {!isCollapsed ? (
                    <div className={styles.scrollContainer}>
                        <NewformaTable ariaLabel="Attachments">
                            <NewformaTableBody>
                                {items.map((item) => (
                                    <NewformaTableRow key={item.key}>
                                        <NewformaTableCell style={{ width: "24px" }}>
                                            <Dismiss16Regular
                                                className={styles.removeIcon}
                                                onClick={() => onItemRemove(item)}
                                                data-testid={`project-files-attachment-remove-${item.key}`}
                                            />
                                        </NewformaTableCell>
                                        <NewformaTableCell style={{ width: "24px" }}>
                                            {getAttachmentIcon(item, styles.fileIcon)}
                                        </NewformaTableCell>
                                        <NewformaTableCell flex>
                                            {item.type === "folder" ? (
                                                <span
                                                    className={styles.folderName}
                                                    onClick={() => onFolderClick(item)}
                                                    data-testid={`project-files-attachment-folder-${item.key}`}
                                                >
                                                    {item.name}
                                                </span>
                                            ) : (
                                                <span className={styles.attachmentName}>{item.name}</span>
                                            )}
                                        </NewformaTableCell>
                                        {item.source === "project" ? (
                                            <NewformaTableCell style={{ width: "16px", paddingLeft: "4px" }}>
                                                <Tooltip
                                                    content={
                                                        props.translate(
                                                            "SHARED.ATTACHMENTS.TOOLTIP.PROJECT_ITEM"
                                                        ) as string
                                                    }
                                                    relationship="label"
                                                    appearance="inverted"
                                                    withArrow
                                                >
                                                    <span>
                                                        <FolderGlobe16Regular className={styles.projectSourceIcon} />
                                                    </span>
                                                </Tooltip>
                                            </NewformaTableCell>
                                        ) : null}
                                    </NewformaTableRow>
                                ))}
                            </NewformaTableBody>
                        </NewformaTable>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default withLocalize(AttachmentsListFluent9Inner);
